import React from 'react';

import {
    CheckboxMenu,
    CheckboxMenuProps,
    ItemProps,
    ValuesObject,
} from 'src/componentes/CheckboxMenu';
import { objectFromEntries } from 'src/utilidades';

export type DatosFiltro = {
    readonly items: readonly ItemProps[];
    readonly itemsVisibles: readonly ItemProps[];
    readonly values: Readonly<Record<string, boolean>>;
};

export const datosFiltroInicial: DatosFiltro = {
    items: [],
    itemsVisibles: [],
    values: {},
};

type Props = Readonly<{
    titulo: readonly [string, string];
    datos: DatosFiltro;
    cuandoDatosCambian: (datos: DatosFiltro) => void;
}>;

export const Filtro = (props: Props) => {
    const { titulo, datos, cuandoDatosCambian } = props;

    return (
        <CheckboxMenu
            title={formatearTitulo(...titulo)}
            items={datos.itemsVisibles}
            values={datos.values}
            onChange={values => cuandoDatosCambian({ ...datos, values })}
        />
    );
};

type DescriptorDeNuevoItem = ItemProps;

type SelectorItem<T> = (elemento: T) => DescriptorDeNuevoItem | readonly DescriptorDeNuevoItem[];
type SelectorValue<T> = (elemento: T) => string | readonly string[];

type Selectores<T> = { [key: string]: SelectorItem<T> };

export const Filtros = {
    inicializar<T, M extends Selectores<T>>(
        elementos: readonly T[],
        selectores: M,
        comparadores?: { [K in keyof M]?: (a: ItemProps, b: ItemProps) => number },
    ) {
        const mapas = objectFromEntries(
            Object.keys(selectores).map(key => [key, new Map<string, DescriptorDeNuevoItem>()]),
        );

        for (const elemento of elementos) {
            for (const [key, selector] of Object.entries(selectores)) {
                for (const descriptor of applySelector(selector, elemento)) {
                    mapas[key].set(descriptor.value, descriptor);
                }
            }
        }

        return objectFromEntries(
            Object.entries(mapas).map(([key, mapa]) => {
                const items: ItemProps[] = [];
                const values: Record<string, boolean> = {};

                for (const descriptor of mapa.values()) {
                    items.push(descriptor);
                    values[descriptor.value] = true; // checked por defecto!
                }

                if (comparadores?.[key]) {
                    const comparador = comparadores[key]!;
                    items.sort(comparador);
                }

                return [
                    //
                    key as Extract<keyof M, string>,
                    { items, itemsVisibles: items, values },
                ];
            }),
        ) as {
            [K in Extract<keyof M, string>]: DatosFiltro;
        };
    },

    filtrar<T>(datosFiltro: DatosFiltro, elementos: readonly T[], selector: SelectorValue<T>): T[] {
        return elementos.filter(elemento => {
            const ids = applySelector(selector, elemento);
            return (
                datosFiltro.items.some(item => ids.includes(item.value)) &&
                ids.some(id => datosFiltro.values[id])
            );
        });
    },

    recalcularItemsVisibles<T>(
        datosFiltro: DatosFiltro,
        elementos: readonly T[],
        selector: SelectorValue<T>,
    ): DatosFiltro {
        const itemsVisibles = datosFiltro.items.filter(item =>
            elementos.some(elemento => applySelector(selector, elemento).includes(item.value)),
        );

        return {
            ...datosFiltro,
            itemsVisibles,
        };
    },
};

export function selectorItemASelectorValue<T>(selector: SelectorItem<T>): SelectorValue<T> {
    return elemento => applySelector(selector, elemento).map(item => item.value);
}

function formatearTitulo<T extends ValuesObject>(
    nombre: string,
    plural: string,
): (props: CheckboxMenuProps<T>) => React.ReactNode {
    return props => {
        const checkedCount = props.items.filter(item => props.values[item.value]).length;
        const uncheckedCount = props.items.length - checkedCount;

        return uncheckedCount === 0 ? (
            nombre
        ) : (
            <b style={{ color: checkedCount === 0 ? 'red' : undefined }}>
                Mostrando solo de {checkedCount}{' '}
                {checkedCount === 1 ? nombre.toLocaleLowerCase() : plural}
            </b>
        );
    };
}

function applySelector<T, U>(selector: (value: T) => U | readonly U[], value: T): U[] {
    const sel = selector(value);
    return Array.isArray(sel) ? sel.slice() : [sel];
}



// WEBPACK FOOTER //
// ./src/modulos/Horarios/componentes/Filtro.tsx